/**
 * useFileUpload — generic file-upload hook
 *
 * Wraps `fileService.uploadFile` with imperative state so any feature can
 * trigger an upload from a file-input onChange, drag-drop handler, etc.
 *
 * Usage:
 *   const { upload, isUploading, error, reset } = useFileUpload();
 *   const { id } = await upload(file);   // id = FileAsset UUID
 */

import { useState, useCallback } from 'react';
import { uploadFile } from '../services/api/fileService';
import type { UploadFileResponse } from '../services/api/fileService';

export interface UseFileUploadReturn {
  /** Uploads a file and returns `{ id }` (the FileAsset UUID). Throws on error. */
  upload: (file: File) => Promise<UploadFileResponse>;
  /** True while the upload request is in flight. */
  isUploading: boolean;
  /** Non-null when the most recent upload attempt failed. */
  error: Error | null;
  /** Clears `error` and resets `isUploading` to false. */
  reset: () => void;
}

export function useFileUpload(): UseFileUploadReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const upload = useCallback(async (file: File): Promise<UploadFileResponse> => {
    setIsUploading(true);
    setError(null);

    try {
      const result = await uploadFile(file);
      return result;
    } catch (err) {
      const uploadError = err instanceof Error ? err : new Error(String(err));
      setError(uploadError);
      throw uploadError;
    } finally {
      setIsUploading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setIsUploading(false);
  }, []);

  return { upload, isUploading, error, reset };
}
